// Pure reducer for the kiosk state machine. Events that don't apply to the
// current phase are no-ops (returns the same state reference) so unrelated
// signals like a stray `bridge-disconnected` mid-print don't drop us out
// of a critical screen.

import {
  declineGloss,
  hintKeyForDecline,
  NO_POS_COMMUNICATION,
} from "./glosses";
import type { KioskEvent, KioskState, Quantities } from "./types";

export const initialState: KioskState = { phase: "idle" };

export function reduce(state: KioskState, event: KioskEvent): KioskState {
  if (event.type === "reset") {
    return initialState;
  }
  if (event.type === "bridge-disconnected") {
    return {
      phase: "out-of-service",
      reason: event.detail ?? "Bridge desconectado",
    };
  }

  switch (state.phase) {
    case "idle":
      if (event.type === "start") {
        return { phase: "select-date" };
      }
      return state;

    case "select-date":
      // Picking a date moves straight into the loading phase; the fetch
      // that drives select-tickets is kicked off by App.tsx's phase-keyed
      // effect when it sees "loading-ticket-types".
      if (event.type === "set-date") {
        return { phase: "loading-ticket-types", date: event.date };
      }
      if (event.type === "cancel") {
        return initialState;
      }
      return state;

    case "loading-ticket-types":
      if (event.type === "ticket-types-loaded") {
        return {
          phase: "select-tickets",
          date: state.date,
          ticketTypes: event.ticketTypes,
          quantities: {},
        };
      }
      if (event.type === "cancel") {
        return initialState;
      }
      // A fetch failure dispatches bridge-disconnected, which is handled at
      // the top of reduce() and routes to out-of-service.
      return state;

    case "select-tickets":
      if (event.type === "set-quantity") {
        return {
          ...state,
          quantities: setQuantity(
            state.quantities,
            event.ticketTypeId,
            event.quantity
          ),
        };
      }
      if (event.type === "go-to-review" && hasAnyQuantity(state.quantities)) {
        return {
          phase: "review",
          date: state.date,
          ticketTypes: state.ticketTypes,
          quantities: state.quantities,
        };
      }
      if (event.type === "go-back" || event.type === "cancel") {
        return { phase: "select-date" };
      }
      return state;

    case "review":
      if (event.type === "submit-order") {
        return {
          phase: "creating-order",
          date: state.date,
          ticketTypes: state.ticketTypes,
          quantities: state.quantities,
        };
      }
      if (event.type === "go-back") {
        return {
          phase: "select-tickets",
          date: state.date,
          ticketTypes: state.ticketTypes,
          quantities: state.quantities,
        };
      }
      if (event.type === "cancel") {
        return initialState;
      }
      return state;

    case "creating-order":
      if (event.type === "order-created") {
        return {
          phase: "awaiting-card",
          orderId: event.orderId,
          amountCLP: event.amountCLP,
          ticketNumber: event.ticketNumber,
        };
      }
      if (event.type === "order-failed") {
        return {
          phase: "payment-failed",
          orderId: null,
          reason: event.reason,
          allowRetry: true,
        };
      }
      return state;

    case "awaiting-card":
    case "card-processing":
      if (event.type === "sale-status") {
        return {
          phase: "card-processing",
          orderId: state.orderId,
          amountCLP: state.amountCLP,
          ticketNumber: state.ticketNumber,
          statusCode: event.code,
          statusMessage: event.message,
        };
      }
      if (event.type === "sale-result") {
        return reduceSaleResult(state.orderId, event.outcome);
      }
      if (event.type === "sale-unverified") {
        // The terminal answered but we could not read its verdict: the card
        // may have been charged. Never offer a retry that could charge twice.
        return {
          phase: "payment-failed",
          orderId: state.orderId,
          reason: NO_POS_COMMUNICATION,
          allowRetry: false,
          hint: "hintVerifyCharge",
          showOrderId: true,
        };
      }
      return state;

    case "confirming":
      if (event.type === "confirm-result") {
        return {
          phase: "printing",
          orderId: state.orderId,
          tickets: event.tickets,
          voucher: event.voucher,
          last4: state.im30.last4 ?? null,
          printedCount: 0,
        };
      }
      if (event.type === "confirm-failed") {
        return {
          phase: "payment-failed",
          orderId: state.orderId,
          reason: "confirmFailedReason",
          allowRetry: false,
          hint: "hintVerifyCharge",
          showOrderId: true,
        };
      }
      return state;

    case "printing":
      if (event.type === "print-progress") {
        return { ...state, printedCount: event.printedCount };
      }
      if (event.type === "print-completed") {
        return { phase: "done", orderId: state.orderId, tickets: state.tickets };
      }
      if (event.type === "print-failed") {
        return {
          phase: "print-error",
          orderId: state.orderId,
          tickets: state.tickets,
          voucher: state.voucher,
          last4: state.last4,
          printedCount: state.printedCount,
          error: event.error,
        };
      }
      return state;

    case "print-error":
      if (event.type === "print-progress") {
        return {
          phase: "printing",
          orderId: state.orderId,
          tickets: state.tickets,
          voucher: state.voucher,
          last4: state.last4,
          printedCount: event.printedCount,
        };
      }
      if (event.type === "print-completed") {
        return { phase: "done", orderId: state.orderId, tickets: state.tickets };
      }
      return state;

    case "done":
      if (event.type === "idle-timeout") {
        return initialState;
      }
      return state;

    case "payment-failed":
      if (event.type === "start" || event.type === "cancel") {
        return initialState;
      }
      return state;

    case "out-of-service":
      return state;

    default:
      return state;
  }
}

function setQuantity(
  quantities: Quantities,
  ticketTypeId: number,
  quantity: number
): Quantities {
  const next = { ...quantities };
  if (quantity <= 0) {
    delete next[ticketTypeId];
  } else {
    next[ticketTypeId] = quantity;
  }
  return next;
}

function hasAnyQuantity(quantities: Quantities): boolean {
  for (const q of Object.values(quantities)) {
    if (q > 0) {
      return true;
    }
  }
  return false;
}

function reduceSaleResult(
  orderId: number,
  outcome: import("@entradart/kiosk-shared/bridge").SaleOutcome
): KioskState {
  if (outcome.outcome === "approved") {
    return { phase: "confirming", orderId, im30: outcome.response };
  }
  if (outcome.outcome === "declined") {
    const hint = hintKeyForDecline(outcome.response.responseCode);
    return {
      phase: "payment-failed",
      orderId,
      reason: declineGloss(outcome.response.responseCode),
      allowRetry: true,
      ...(hint ? { hint } : {}),
    };
  }
  if (outcome.outcome === "timeout") {
    // Casos 25/26 del set de pruebas: el POS no respondió (se quedó sin
    // electricidad o el cable se soltó a mitad de venta). Transbank exige
    // esta glosa exacta y que la venta quede liberada para reintentar.
    return {
      phase: "payment-failed",
      orderId,
      reason: NO_POS_COMMUNICATION,
      allowRetry: true,
    };
  }
  if (outcome.outcome === "cancelled") {
    return {
      phase: "payment-failed",
      orderId,
      reason: "Transacción cancelada",
      allowRetry: true,
    };
  }
  // link_error: no había terminal alcanzable al iniciar la venta (caso 23).
  // No fue un fallo del kiosco completo — bridge-disconnected sigue llevando
  // a out-of-service — así que se libera la venta con la glosa exigida en
  // vez de sacar el tótem de servicio: reconectado el cable, el reintento
  // vuelve a enviar la venta al POS.
  return {
    phase: "payment-failed",
    orderId,
    reason: NO_POS_COMMUNICATION,
    allowRetry: true,
  };
}

// --- Selectors ---

export function totalAmount(
  state: Extract<
    KioskState,
    { phase: "select-tickets" | "review" | "creating-order" }
  >
): number {
  let total = 0;
  for (const tt of state.ticketTypes) {
    const q = state.quantities[tt.id] ?? 0;
    total += tt.priceCLP * q;
  }
  return total;
}

export function totalTickets(quantities: Quantities): number {
  let total = 0;
  for (const q of Object.values(quantities)) {
    total += q;
  }
  return total;
}
